/**
 * UrlTexture - texture that loads from a URL with Genesys serialization support.
 * Defers loading to TextureLoader, provides 1x1 placeholder during async load.
 */

import * as THREE from 'three';

import { DISABLE_TEXTURE_FLIP } from '../const.js';
import { EngineClass } from '../systems/ClassRegistry.js';
import { getLatest } from '../utils/Async.js';
import { property } from '../utils/serialization/decorator.js';
import { ISerializableObjectTag } from '../utils/serialization/serializable.js';
import { isNode } from '../utils/Utils.js';

import type { TexturePath } from '../utils/serialization/decorator.js';
import type { IDumper, ILoader } from '../utils/serialization/factories.js';

/**
 * Options for the UrlTexture constructor
 */
export interface UrlTextureOptions {
  url?: string;
  mapping?: THREE.Mapping;
  wrapS?: THREE.Wrapping;
  wrapT?: THREE.Wrapping;
  magFilter?: THREE.MagnificationTextureFilter;
  minFilter?: THREE.MinificationTextureFilter;
  format?: THREE.PixelFormat;
  type?: THREE.TextureDataType;
  anisotropy?: number;
  colorSpace?: THREE.ColorSpace;
  repeat?: THREE.Vector2;
  offset?: THREE.Vector2;
  rotation?: number;
}

export type UrlTextureLoader = (url: string) => Promise<THREE.Texture | null>;

let urlTextureLoader: UrlTextureLoader | null = null;

export function setUrlTextureLoader(loader: UrlTextureLoader): void {
  urlTextureLoader = loader;
}

async function loadTextureViaRegisteredLoader(url: string): Promise<THREE.Texture | null> {
  if (!urlTextureLoader) {
    console.trace('[UrlTexture] No texture loader has been registered');
    return null;
  }
  try {
    return await urlTextureLoader(url);
  } catch (error) {
    console.error('[UrlTexture] Failed to load texture', error);
    return null;
  }
}

/**
 * A texture that loads from a URL
 */
@EngineClass('UrlTexture')
export class UrlTexture extends THREE.Texture {
  // the default texture used to determine which fields on the current texture are modified
  private static readonly defaultTexture = new THREE.Texture();
  private static readonly copyableFields: (keyof THREE.Texture)[] = [
    'mapping',
    'channel',
    'wrapS',
    'wrapT',
    'magFilter',
    'minFilter',
    'anisotropy',
    'format',
    'type',
    'internalFormat',
    'matrix',
    'matrixAutoUpdate',
    'offset',
    'repeat',
    'center',
    'rotation',
    'generateMipmaps',
    'premultiplyAlpha',
    'flipY',
    'unpackAlignment',
    'colorSpace',
    'isRenderTargetTexture',
    'isArrayTexture',
    'updateRanges',
    'version',
    'pmremVersion',
    'normalized',
    // 'userData',
  ];

  private static readonly textureTypeFlags: string[] = [
    'isTexture',
    'isRenderTargetTexture',
    'isArrayTexture',
    'isCubeTexture',
    'isCubeDepthTexture',
    'isCompressedCubeTexture',
    'isDataTexture',
    'isData3DTexture',
    'isDataArrayTexture',
    'isDepthTexture',
    'isCanvasTexture',
    'isHTMLTexture',
    'isCompressedTexture',
    'isCompressedArrayTexture',
    'isVideoTexture',
    'isVideoFrameTexture',
    'isFramebufferTexture',
    'isExternalTexture'
  ];

  readonly [ISerializableObjectTag] = true;
  public loadPromise: Promise<THREE.Texture | null> = Promise.resolve(null);

  private _url: string = '';

  @property()
  get url(): TexturePath { return this._url; }

  set url(value: string) {
    this._url = value;
    if (isNode()) {
      return;
    }
    const loadTexture = async () => {
      const result = await getLatest(
        this, 'texture',
        () => loadTextureViaRegisteredLoader(this._url)
      );
      return result ?? null;
    };
    this.loadPromise = loadTexture().catch((error) => {
      console.error('[UrlTexture] Unexpected texture load error', error);
      return null;
    });
    this.loadPromise.then((texture) => {
      if (texture) {
        this.copyFromSourceTexture(texture);
      }
    });
  }

  constructor(options: UrlTextureOptions = {}) {
    super(
      undefined,
      options.mapping,
      options.wrapS,
      options.wrapT,
      options.magFilter,
      options.minFilter,
      options.format,
      options.type,
      options.anisotropy
    );

    if (DISABLE_TEXTURE_FLIP) {
      this.flipY = false;
    }

    // Provide a 1x1 transparent placeholder so Three.js WebGPU never reads null texture.image.
    // Three.js r183 Textures.updateTexture reads image.complete which throws if image is null.
    // The placeholder is replaced with the real image once the async URL load completes.
    if (typeof document !== 'undefined') {
      const canvas = document.createElement('canvas');
      canvas.width = 1;
      canvas.height = 1;
      this.image = canvas;
    }

    // Set additional texture properties from options
    if (options.colorSpace !== undefined) this.colorSpace = options.colorSpace;
    if (options.repeat !== undefined) this.repeat.copy(options.repeat);
    if (options.offset !== undefined) this.offset.copy(options.offset);
    if (options.rotation !== undefined) this.rotation = options.rotation;

    if (options.url) this.url = options.url;
  }

  public serialize(dumper: IDumper): void {
    if (!dumper.dumpingAsPrefab) {
      dumper.dumpValue('$uuid', this.uuid);
    }
    dumper.dumpProperties(this);
  }

  public deserialize(loader: ILoader): void {
    (this as any).uuid = loader.loadValue('$uuid') ?? this.uuid;
    loader.loadProperties(this);
  }

  private copyFromSourceTexture(source: THREE.Texture): void {
    // these we need to copy in all cases
    this.image = source.image;
    this.source = source.source;
    this.mipmaps = source.mipmaps.slice(0);

    // check other copyable fields
    for (const field of UrlTexture.copyableFields) {
      const currentValue = this[field];
      const defaultValue = UrlTexture.defaultTexture[field];
      const backingValue = source[field];
      if (typeof currentValue === 'object') {
        let shouldCopy = false;
        if (currentValue && typeof (currentValue as any).equals === 'function') {
          shouldCopy = (currentValue as any).equals(defaultValue);
        } else {
          shouldCopy = currentValue === defaultValue;
        }
        if (shouldCopy) {
          if (backingValue && typeof (backingValue as any).clone === 'function') {
            (this as any)[field] = (backingValue as any).clone();
          } else {
            (this as any)[field] = backingValue;
          }
        }
      } else {
        // primitive value, not modified, copy from backing texture
        if (currentValue === defaultValue) {
          (this as any)[field] = source[field];
        }
      }
    }

    // copy other type flags
    for (const field of UrlTexture.textureTypeFlags) {
      (this as any)[field] = (source as any)[field];
    }

    this.needsUpdate = !!this.image;
  }
}
